import React, { useEffect, useState } from 'react';
import { CalendarClock, Save } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { buildApiUrl } from '../../utils/apiUrl';
import { inputCls, btnPrimary, pageTitleCls, pageSubtitleCls } from '../../components/ui';

interface ParametroCronologia {
  id: number;
  ciclo: 'PC' | 'RC';
  campo_desde: string;
  campo_hasta: string;
  dias_min: number;
  dias_max: number;
  descripcion: string;
  activo: boolean;
}

// Nombre legible de cada columna de fecha-ancla, igual al resto de Estadios.
const ETIQUETA_CAMPO: Record<string, string> = {
  fecha_siembra: 'Siembra',
  fecha_induccion: 'Inducción',
  fecha_forza: 'Forza',
  fecha_maduracion: 'Maduración',
  fecha_cosecha: 'Cosecha',
  fecha_semillero: 'Semillero',
  fecha_segunda: '2ª Siembra',
  fecha_induccion2: '2ª Inducción',
  fecha_forza2: '2ª Forza',
  fecha_maduracion2: '2ª Maduración',
  fecha_cosecha2: '2ª Cosecha',
};

export function CronologiaParametrosPage() {
  const empresaId = useEmpresaId();
  const { perm_vob } = useAppPermisos();

  const [parametros, setParametros] = useState<ParametroCronologia[]>([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/estadios/cronologia-parametros'), {
        headers: {
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const j = await resp.json().catch(() => ({}));
      if (!vivo) return;
      if (resp.ok && Array.isArray(j.parametros)) setParametros(j.parametros);
      setLoading(false);
    })();
    return () => { vivo = false; };
  }, [empresaId]);

  function set(id: number, campo: 'dias_min' | 'dias_max', valor: number) {
    setParametros(prev => prev.map(p => p.id === id ? { ...p, [campo]: valor } : p));
  }

  function toggleActivo(id: number) {
    setParametros(prev => prev.map(p => p.id === id ? { ...p, activo: !p.activo } : p));
  }

  async function guardar() {
    setGuardando(true);
    try {
      const invalido = parametros.find(p => p.dias_min < 0 || p.dias_max < p.dias_min);
      if (invalido) {
        setToast({ msg: `Rango inválido en "${invalido.descripcion}": el mínimo no puede ser mayor que el máximo.`, ok: false });
        setGuardando(false);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/estadios/cronologia-parametros'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          parametros: parametros.map(p => ({ id: p.id, dias_min: p.dias_min, dias_max: p.dias_max, activo: p.activo })),
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo guardar');
      setToast({ msg: 'Parámetros actualizados.', ok: true });
    } catch (err: any) {
      setToast({ msg: err?.message || 'Error al guardar', ok: false });
    } finally {
      setGuardando(false);
    }
  }

  const porCiclo: Record<string, ParametroCronologia[]> = { PC: [], RC: [] };
  for (const p of parametros) (porCiclo[p.ciclo] ??= []).push(p);

  if (!perm_vob) {
    return (
      <div className="p-6">
        <h1 className={pageTitleCls}>Cronología del cultivo</h1>
        <p className={pageSubtitleCls}>Ajustar estos rangos requiere autorización de ingeniería (V°B°).</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock size={18} style={{ color: 'var(--emp-accent-txt)' }} />
        <h1 className={pageTitleCls}>Cronología del cultivo — Parámetros</h1>
      </div>
      <p className={pageSubtitleCls}>
        Días esperados entre cada fecha-ancla del ciclo. Estadios avisa cuando el tiempo entre dos
        fases se sale de este rango (además de validar el orden). Los valores iniciales vienen de
        investigación general de piña MD2 en Costa Rica — ajustalos según la experiencia real de la finca.
      </p>

      {loading ? (
        <p className="mt-6 text-sm" style={{ color: 'var(--ink-faint)' }}>Cargando…</p>
      ) : (
        <div className="mt-6 space-y-6">
          {(['PC', 'RC'] as const).map(ciclo => (
            porCiclo[ciclo].length > 0 && (
              <div key={ciclo} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
                <div className="px-4 py-2 text-xs font-bold uppercase tracking-wide" style={{ background: 'var(--surface-raised)', color: 'var(--ink-muted)' }}>
                  {ciclo === 'PC' ? 'Primera Cosecha (PC)' : 'ReCosecha (RC)'}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--line-dim)' }}>
                      <th className="text-center px-3 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Activo</th>
                      <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Desde</th>
                      <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Hasta</th>
                      <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Mín. (días)</th>
                      <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Máx. (días)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porCiclo[ciclo].map(p => (
                      <tr key={p.id} title={p.descripcion} style={{ borderBottom: '1px solid var(--line-dim)', opacity: p.activo ? 1 : 0.55 }}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={p.activo} onChange={() => toggleActivo(p.id)}
                            title={p.activo ? 'Desactivar (deja de avisar en Estadios)' : 'Activar (empieza a avisar en Estadios)'} />
                        </td>
                        <td className="px-4 py-2" style={{ color: 'var(--ink)' }}>
                          {ETIQUETA_CAMPO[p.campo_desde] || p.campo_desde}
                          {!p.activo && <span className="ml-1.5 text-[10px]" style={{ color: 'var(--ink-faint)' }}>(sin activar)</span>}
                        </td>
                        <td className="px-4 py-2" style={{ color: 'var(--ink)' }}>{ETIQUETA_CAMPO[p.campo_hasta] || p.campo_hasta}</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" min={0} className={`${inputCls} text-right w-24 inline-block`}
                            value={p.dias_min} onChange={e => set(p.id, 'dias_min', Number(e.target.value))} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" min={0} className={`${inputCls} text-right w-24 inline-block`}
                            value={p.dias_max} onChange={e => set(p.id, 'dias_max', Number(e.target.value))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ))}

          {parametros.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
              Sin parámetros configurados para esta empresa todavía.
            </p>
          )}

          <button onClick={guardar} disabled={guardando || parametros.length === 0}
            className={btnPrimary}
            style={{ opacity: (guardando || parametros.length === 0) ? 0.6 : 1 }}>
            <Save size={14} className="inline mr-1.5 -mt-0.5" />
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
          style={{
            background: toast.ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
            border: `1px solid ${toast.ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
            color: toast.ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
          }}>
          {toast.msg}
          <button className="ml-3 opacity-70" onClick={() => setToast(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
